import React, { useEffect, useState } from 'react';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { toFlagId, useFlagsService } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { NOT_LOADED, NO_VALUE, useProperties } from '../services/use-properties/index.tsx';
import { FlagInner } from './flag-inner/index.tsx';
import { UrlUpdateDialog } from './url-update-dialog/index.tsx';

export const HIDDEN = 'HIDDEN';
export const DISMISSED = 'DISMISSED';
export const MILLISECONDS_IN_A_DAY = 24 * 60 * 1000;

export type ModalState = typeof HIDDEN | typeof DISMISSED;

const CUSTOM_DOMAIN_CHANGE_BOARDING_FLAG_ID = toFlagId('jsm-custom-domain-change-boarding-flag');
const deactivatedLearnMoreLink =
	'https://support.atlassian.com/organization-administration/docs/remove-or-deactivate-a-custom-domain/';

const JsmCustomDomainChangeBoardingFlag = () => {
	const currentTime = Date.now();
	const { showFlag, dismissFlag } = useFlagsService();
	const [flagShown, setFlagShown] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<boolean>(false);
	const [modalState, setModalState] = useState<ModalState>(HIDDEN);

	const [
		{ userSeenFlagDate, showFlagUpdateStartDate, helpCenterUrls, customDomainStatus },
		{ updateUserProperty, fetchApplicationProperty, fetchSeenFlagDateProperty },
	] = useProperties();

	const onDismissed = (id: string | number, analyticsEvent: UIAnalyticsEvent) => {
		dismissFlag(CUSTOM_DOMAIN_CHANGE_BOARDING_FLAG_ID);
		fireUIAnalytics(analyticsEvent, 'customDomainChangeBoardingFlag');
		updateUserProperty();
	};

	const onClick = () => {
		if (customDomainStatus === 'ACTIVE') {
			setShowModal(true);
			setFlagShown(true);
			dismissFlag(CUSTOM_DOMAIN_CHANGE_BOARDING_FLAG_ID);
		}
		if (customDomainStatus === 'INACTIVE') {
			updateUserProperty();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(deactivatedLearnMoreLink, '_blank');
		}
	};

	const onClose = (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		setShowModal(false);
		setModalState(DISMISSED);
		fireUIAnalytics(analyticsEvent, 'customDomainChangeBoardingGotIt');
		updateUserProperty();
	};

	const timeStampToDate = (timestamp: number): Date => new Date(timestamp);

	useEffect(() => {
		Promise.all([fetchApplicationProperty(), fetchSeenFlagDateProperty()]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const pollInterval = setInterval(
			() => Promise.all([fetchApplicationProperty(), fetchSeenFlagDateProperty()]),
			MILLISECONDS_IN_A_DAY,
		);
		return () => clearInterval(pollInterval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (showModal && helpCenterUrls)
		return (
			<ModalTransition>
				<UrlUpdateDialog onClose={onClose} helpCenterUrls={helpCenterUrls} />
			</ModalTransition>
		);

	if (modalState === DISMISSED) return <ModalTransition />;
	if (flagShown || helpCenterUrls === undefined || customDomainStatus === undefined) return null;
	if (userSeenFlagDate === NOT_LOADED || showFlagUpdateStartDate === NOT_LOADED) return null;
	if (showFlagUpdateStartDate === NO_VALUE) return null;

	if (
		isBefore(timeStampToDate(currentTime), timeStampToDate(showFlagUpdateStartDate)) ||
		(userSeenFlagDate !== NO_VALUE &&
			isAfter(timeStampToDate(userSeenFlagDate), timeStampToDate(showFlagUpdateStartDate)))
	)
		return null;

	showFlag({
		id: CUSTOM_DOMAIN_CHANGE_BOARDING_FLAG_ID,
		render: (props: FlagCustomRenderProps) => (
			<FlagInner
				{...props}
				onDismissed={onDismissed}
				onClick={onClick}
				status={customDomainStatus}
				helpCenterUrls={helpCenterUrls}
				messageId={`help-center-custom-domain-change-boarding.ui.flag-inner.${CUSTOM_DOMAIN_CHANGE_BOARDING_FLAG_ID}`}
				messageType="transactional"
			/>
		),
	});
	setFlagShown(true);

	return null;
};

export default JsmCustomDomainChangeBoardingFlag;
